  import React, { useCallback, useEffect, useRef, useState } from "react";
  import { toast } from "sonner";
  import { ArrowLeft, Check, Ellipsis, Info, LinkIcon, ListTodo, Milk, PackageMinus, PackageSearch, Pencil, ShoppingBasket, Sparkles, Trash } from "lucide-react";
  import { Button } from "@/components/ui/button";
  import { useNavigate, useParams, useLocation } from "react-router-dom";
  import { usePageTitle } from "hooks/usePageTitle";
  import { PageBreadcrumbs } from "modules/main/components/page-breadcrumbs";
  import { AcquirePlusDialog } from "modules/subscription/components/AcquirePlusDialog";
  import { PlanRequiredBody, PlanRequiredError } from "modules/main/services/errors";
  import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
  import { ListItemRow } from "modules/lists/components/ListItemRow";
  import { OptimisticItemRow } from "modules/lists/components/OptimisticItemRow";
  // import { AddItemDialog } from "modules/lists/components/AddItemDialog";
  import { AddDeleteList } from "modules/lists/components/addDeleteLIst";
  import { InlineProductSearch } from "modules/lists/components/InlineProductSearch";
  import { MainFlowSteps } from "modules/lists/components/main-flow-steps";
  import { EmailImportCard } from "modules/lists/components/email-import-card";
  import { EmailImportDialog } from "modules/lists/components/email-import-dialog";
  import { CreateListItemPayload, ListItem, ListItemType, OptimisticListItem, TemporaryQuoteItem } from "modules/lists/types/lists";
  import { useLists } from "modules/lists/context/ListsContext";
  import { useItemAutosave } from "modules/lists/hooks/useItemAutosave";
  import MenuIcon from "modules/main/types/menus";
  import { groupListItemsByCategory } from "modules/lists/utils/listCategories";
  import { useProgressStream } from "modules/lists/hooks/useProgressStream";
  import { ListExecuteProgressOverlay } from "modules/lists/components/ListExecuteProgressOverlay";
  import { useMigrationStream } from "modules/lists/hooks/useMigrationStream";
  import { ListMigrateProgressOverlay } from "modules/lists/components/ListMigrateProgressOverlay";
  import { useCart } from "modules/cart/hooks/useCart";
  import { useCartSheet } from "hooks/useCartSheet";
  import { consumeOnboardingTargetPrompt, OnboardingTargetPrompt } from "modules/lists/utils/onboarding-target-prompt";
  import { clearEmailImportOpened } from "modules/lists/utils/email-import-flag";

  import { triggerBatchProductUpdates } from "modules/product/services/getUpdates";
  import {
      Empty,
      EmptyDescription,
      EmptyHeader,
      EmptyMedia,
      EmptyTitle,
  } from "@/components/ui/empty"
  import { Badge } from "@/components/ui/badge";
  import { SlotText } from "slot-text/react";
import posthog from "posthog-js";
  import "slot-text/style.css";
  import {
      DropdownMenu,
      DropdownMenuContent,
      DropdownMenuItem,
      DropdownMenuTrigger,
  } from "@/components/ui/dropdown-menu";
  import { useMediaQuery } from "@/hooks/use-media-query";
  import { isDesktopSize } from "utils/is-desktop";
  import { cn } from "@/lib/utils";
  /**
   * Page that displays and manages a single list's detail (items, name, description).
   * Route: /lists/:listUuid — view/edit mode for an existing list
   */
  const ListDetailPage: React.FC = () => {
      const { setTitle, setShowBack, setHeaderAction, setShowCart, setShowHelp, setHideHeaderTitle, setBreadcrumb } = usePageTitle();
      const { listUuid } = useParams<{ listUuid: string }>();
      const navigate = useNavigate();
      const location = useLocation();
      const isDesktop = useMediaQuery(isDesktopSize);
      const { lists, updateList, addItem, deleteItem, deleteList, loadListDetail, seedListTemplate } = useLists();

      const { cart, clearCart, applyListExecution, setResolutionMode, addDeletefromCartWithTracking } = useCart();

      // Mutable ref to avoid closure staleness in handleExecuteList setTimeout callback.
      // cart length is checked via ref so the deferred re-invocation always sees the
      // latest cart state after clearCart() has been called.
      const cartRef = useRef(cart);
      cartRef.current = cart;

      /** Latest lists snapshot — avoids stale closures inside async handlers. */
      const listsRef = useRef(lists);
      listsRef.current = lists;
      /** Previous hydrated items length — used to detect a list becoming empty. */
      const prevItemsLengthRef = useRef<number | null>(null);

      const { handleEditItem: autosaveEditItem, saveErrors } = useItemAutosave(listUuid);

      const selectedList = lists.find((l) => l.id === listUuid) ?? null;

      const [isEditMode, setIsEditMode] = useState(false);
      const [isSavingList, setIsSavingList] = useState(false);
      const [isHydratingListDetail, setIsHydratingListDetail] = useState(true);
      const [editName, setEditName] = useState("");
      const [addItemOpen, setAddItemOpen] = useState(false);
      const [editingItem, setEditingItem] = useState<ListItem | null>(null);
      const { open: openCartSheet, setOpen: setOpenCartSheet } = useCartSheet();
      const [isExecuting, setIsExecuting] = useState(false);
      /** SSE execution stream for the in-flight execution (single request). */
      const { progress, startProgress } = useProgressStream();
      const [showReplaceDialog, setShowReplaceDialog] = useState(false);
      const [showDeleteListDialog, setShowDeleteListDialog] = useState(false);
      const [isDeletingList, setIsDeletingList] = useState(false);
      const [onboardingTargets, setOnboardingTargets] = useState<OnboardingTargetPrompt | null>(null);
      /** Cut 3: backend quota block body — opens AcquirePlusDialog with quota copy. */
      const [quotaError, setQuotaError] = useState<PlanRequiredBody | null>(null);

      // ── Quick-entry: email import + seed example list ──────────────────────
      /** Owns the single controlled email-import dialog (menu + card entry). */
      const [emailImportOpen, setEmailImportOpen] = useState(false);
      /** Confirmation gate before seeding a non-empty list (seed may duplicate). */
      const [showSeedConfirm, setShowSeedConfirm] = useState(false);
      /** Disables feedback while a seed request is in flight. */
      const [isSeeding, setIsSeeding] = useState(false);
      /**
       * Duplicate-submit guard for seeding. The ref is set synchronously before
       * the first await so rapid re-triggers are ignored even before React
       * re-renders with `isSeeding = true`.
       */
      const isSeedingRef = useRef(false);

      // ── Intent migration (separate SSE channel from execution) ─────────────
      /**
       * Lists that finished a PARTIAL migration this session. After a partial,
       * the reloaded detail still reports migration_required (items keep NULL
       * embeddings), so this guard prevents the stream from re-opening
       * automatically — the spec forbids infinite auto-retry after partial.
       */
      const [partialListIds, setPartialListIds] = useState<Set<string>>(new Set());

      /** Terminal completed: close the overlay by reloading the detail. */
      const handleMigrationCompleted = useCallback(() => {
          if (listUuid) void loadListDetail(listUuid);
      }, [listUuid, loadListDetail]);

      /** Terminal partial: warn, guard against auto-retry, reload detail. */
      const handleMigrationPartial = useCallback((failedCount: number) => {
          if (listUuid) {
              setPartialListIds((prev) => new Set(prev).add(listUuid));
              void loadListDetail(listUuid);
          }
          toast.warning(
              failedCount > 0
                  ? `La migración quedó incompleta (${failedCount} producto${failedCount !== 1 ? "s" : ""}).`
                  : "La migración quedó incompleta.",
          );
      }, [listUuid, loadListDetail]);

      /**
       * Auto-start the migration stream only when the detail says migration is
       * required AND this list did not just finish a partial migration.
       */
      const migrationEnabled =
          selectedList?.migrationRequired === true &&
          !partialListIds.has(listUuid ?? "");

      const migrationStream = useMigrationStream({
          listId: listUuid ?? "",
          enabled: migrationEnabled,
          onCompleted: handleMigrationCompleted,
          onPartial: handleMigrationPartial,
          onError: () => {
              // Stream failed before a terminal event — the overlay shows the
              // Spanish error state with a manual retry button.
          },
      });

      /**
       * Overlay visibility: visible while streaming (or idle/pending) and in
       * the error state; hidden after terminal completed/partial events.
       */
      const showMigrationOverlay =
          selectedList?.migrationRequired === true &&
          migrationStream.status !== "completed" &&
          migrationStream.status !== "partial";

      // View-mode checklist state — local only, never persisted to DB
      const [checkedItemIds, setCheckedItemIds] = useState<Set<string>>(new Set());
      /** Temporary products added for quoting purposes only. Never persisted to the list. */
      const [temporaryItems, setTemporaryItems] = useState<TemporaryQuoteItem[]>([]);
      /** Products added in edit mode whose backend save is still in flight or failed. */
      const [optimisticItems, setOptimisticItems] = useState<OptimisticListItem[]>([]);
      const [localQuantities, setLocalQuantities] = useState<Record<string, number>>({});
      /** Snapshot for the "Deshacer" undo when entering edit mode with checked items. */
      const checkedSnapshot = useRef<{ ids: Set<string>; qtys: Record<string, number> } | null>(null);
      /**
       * Tracks in-flight addItem requests fired by InlineProductSearch (optimistic adds).
       * Used to wait for all pending saves before persisting the list, so a quick
       * "Guardar lista" doesn't race with adds that are still being processed.
       */
      const pendingInlineAddPromisesRef = useRef<Set<Promise<void>>>(new Set());

      /**
       * Resolves once every tracked inline add has settled. Uses a loop because a new
       * add can be registered while we are already awaiting (user keeps typing).
       * allSettled ensures a failed add doesn't block or break the save.
       */
      const waitForPendingInlineAdds = useCallback(async () => {
          while (pendingInlineAddPromisesRef.current.size > 0) {
              const pending = Array.from(pendingInlineAddPromisesRef.current);
              await Promise.allSettled(pending);
          }
      }, []);

      // ── Edit handlers ──────────────────────────────────────────────

      const handleSaveInlineEdit = useCallback(async () => {
          if (!editName.trim() || !listUuid || isSavingList) return;
          setIsSavingList(true);
          try {
              // Wait for any in-flight product adds before persisting the list
              await waitForPendingInlineAdds();
              // Description field was removed from the UI; preserve whatever the
              // backend already stores so saving the name never wipes it.
              const description = listsRef.current.find((list) => list.id === listUuid)?.description ?? "";
              await updateList(listUuid, editName.trim(), description);
              setLocalQuantities({});
              // An empty list must stay in edit mode so the user can keep
              // adding products; only a list with items returns to view mode.
              const itemsAfterSave = listsRef.current.find((list) => list.id === listUuid)?.items.length ?? 0;
              if (itemsAfterSave > 0) setIsEditMode(false);
          } finally {
              setIsSavingList(false);
          }
      }, [editName, listUuid, isSavingList, updateList, setLocalQuantities, setIsEditMode, waitForPendingInlineAdds]);


      const handleEnterEditMode = useCallback(() => {
          setEditName(selectedList!.name);
          if (checkedItemIds.size > 0) {
              checkedSnapshot.current = {
                  ids: new Set(checkedItemIds),
                  qtys: { ...localQuantities },
              };
              const count = checkedItemIds.size;
              setCheckedItemIds(new Set());
              setLocalQuantities({});
              toast(`${count} producto${count !== 1 ? "s" : ""} omitido${count !== 1 ? "s" : ""} se ${count !== 1 ? "reiniciaron" : "reinició"} al editar`, {
                  position: "bottom-center",
                  duration: 5000,
                  action: {
                      label: "Deshacer",
                      onClick: () => {
                          if (checkedSnapshot.current) {
                              setCheckedItemIds(checkedSnapshot.current.ids);
                              setLocalQuantities(checkedSnapshot.current.qtys);
                              checkedSnapshot.current = null;
                          }
                          setIsEditMode(false);
                      },
                  },
              });
          } else {
              setLocalQuantities({});
          }
          setIsEditMode(true);
      }, [selectedList, checkedItemIds, localQuantities, setEditName, setCheckedItemIds, setLocalQuantities, setIsEditMode]);

      useEffect(() => {
          setTitle(selectedList?.name || "Lista de Compra");
          setShowCart(true);
          setShowHelp(false);
      });

      /**
       * EXTENSION 8 (R3): the list detail page legitimately shows the back
       * arrow, but must clean it up on unmount so the global showBack never
       * leaks onto the next route. Dedicated effect with [setShowBack] (a
       * stable setter) so the true→false pair is symmetric.
       */
      useEffect(() => {
          setShowBack(true);
          return () => setShowBack(false);
      }, [setShowBack]);

      /**
       * Declares the "Inicio > list name" header breadcrumb. Separate effect
       * with explicit deps [selectedList?.name, setBreadcrumb]: a fresh JSX
       * element identity every render would re-set the context every render
       * and loop (design D1). The list-name dep (not the list object) keeps
       * the label in sync on rename without re-render loops.
       */
      useEffect(() => {
          setBreadcrumb(
              <PageBreadcrumbs
                  crumbs={[
                      { label: "Inicio", to: "/store" },
                      { label: selectedList?.name || "Lista de Compra" },
                  ]}
              />,
          );
          return () => setBreadcrumb(null);
      }, [selectedList?.name, setBreadcrumb]);

      useEffect(() => {
          setHeaderAction(null);
      }, []);

      useEffect(() => {
              return () => {
                  setShowCart(true);
                  setShowHelp(true);
                  setHeaderAction(null);
                  setHideHeaderTitle(false);
              };
      }, [setShowCart, setShowHelp, setHeaderAction, setHideHeaderTitle]);

      // Initialise edit fields when the active list changes
      useEffect(() => {
          const startInEditMode = (location.state as { isNew?: boolean } | null)?.isNew === true;
          if (startInEditMode) navigate(location.pathname, { state: {}, replace: true });
          setIsEditMode(startInEditMode);
          setOnboardingTargets(listUuid ? consumeOnboardingTargetPrompt(listUuid) : null);
          setEditName(selectedList?.name ?? "");
          // Reset view-mode checklist and temporary items when navigating to a different list
          setCheckedItemIds(new Set());
          setLocalQuantities({});
          setTemporaryItems([]);
          setOptimisticItems([]);
          setIsHydratingListDetail(true);
          // Reset the empty→edit tracker so a navigation never inherits the
          // previous list's items length.
          prevItemsLengthRef.current = null;
          if (listUuid) {
              void (async () => {
                  try {
                      const loadedDetail = await loadListDetail(listUuid);
                      // Only the fetched detail can prove that a list is empty.
                      // The collection response uses items: [] before hydration,
                      // so falling back to context here would reopen the race.
                      if (!startInEditMode && loadedDetail?.items.length === 0) {
                          setIsEditMode(true);
                      }
                  } finally {
                      setIsHydratingListDetail(false);
                  }
              })();
          } else {
              setIsHydratingListDetail(false);
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [listUuid]);

      /**
       * Auto-activates edit mode whenever a hydrated list ends up with zero
       * products (e.g. the user deletes the last item in view mode). Only fires
       * on a true non-empty → empty transition, so the initial hydration of a
       * non-empty list never briefly opens the editor.
       */
      useEffect(() => {
          if (!selectedList) return;
          const current = selectedList.items?.length;
          if (current === undefined) return;
          const previous = prevItemsLengthRef.current;
          prevItemsLengthRef.current = current;
          if (current === 0 && previous !== null && previous > 0) {
              setIsEditMode(true);
          }
      }, [selectedList]);

      /** Items displayed: real items for existing list */
      const displayItems = selectedList?.items ?? [];
      const showEmptyListState = !isHydratingListDetail && displayItems.length === 0 && optimisticItems.length === 0;

      // The email-import "already sent" flag only matters while the list is empty:
      // once the list has products, drop the stale flag so a future empty state
      // starts fresh. Keyed on the boolean so it runs once per empty→non-empty transition.
      const hasListItems = displayItems.length > 0 || optimisticItems.length > 0;
      useEffect(() => {
          if (hasListItems && listUuid) {
              clearEmailImportOpened(listUuid);
          }
      }, [hasListItems, listUuid]);

      const handleSaveTemporary = useCallback(async () => {
          if (!listUuid) return;
          try {
              for (const temp of temporaryItems) {
                  await addItem(listUuid, {
                      rawDescription: temp.rawDescription ?? temp.name,
                      catalogProductId: temp.catalogProductId,
                      catalogProductSku: temp.sku ?? undefined,
                      categoryId: temp.categoryId,
                      quantity: temp.quantity,
                      name: temp.name,
                  });
              }

              for (const [itemId, qty] of Object.entries(localQuantities)) {
                  const item = displayItems.find(i => i.id === itemId);
                  if (!item) continue;
                  autosaveEditItem(itemId, {
                      rawDescription: item.rawDescription,
                      catalogProductId: item.catalogProductId,
                      catalogProductSku: item.catalogProductSku,
                      quantity: qty,
                      unit: item.unit,
                      notes: item.notes,
                  });
              }

              setTemporaryItems([]);
              setLocalQuantities({});
              toast.success("Cambios guardados en tu lista.");
          } catch {
              toast.error("No se pudieron guardar los cambios.");
          }
      }, [listUuid, temporaryItems, localQuantities, displayItems, addItem, autosaveEditItem]);

      // In view mode, apply local quantity overrides and split into active / excluded groups
      const effectiveItems = displayItems.map((item) => ({
          ...item,
          quantity: (!isEditMode && localQuantities[item.id] !== undefined)
              ? localQuantities[item.id]
              : item.quantity,
      }));
      const activeItems = effectiveItems.filter((item) => !checkedItemIds.has(item.id));
      const checkedItems = effectiveItems.filter((item) => checkedItemIds.has(item.id));
      const executeItemCount = activeItems.length + temporaryItems.length;
      const executeCtaLabel = isExecuting
          ? "Cotizando..."
          : `Cotizar lista · ${executeItemCount} producto${executeItemCount !== 1 ? "s" : ""}`;
      const includedCount = activeItems.length + temporaryItems.length;
      const excludedCount = checkedItems.length;
      const noPreferenceCount = activeItems.filter(i => i.catalogProductId === null).length
          + temporaryItems.filter(t => t.catalogProductId === null).length;
      const fixedBrandCount = activeItems.filter(i => i.catalogProductId !== null).length
          + temporaryItems.filter(t => t.catalogProductId !== null).length;

      /**
       * Executes the list: resolves free-text items via LLM in the background and
       * adds linked items to the cart immediately. Opens the CartSheet when done.
       * Excludes items that the user has marked via checkboxes.
       */
      /**
       * Core execution logic extracted from handleExecuteList.
       * Called directly for empty cart or after user confirms clearing the cart.
       */
      const performExecution = async () => {
          // Abort handle for the SSE execution stream — declared at function
          // scope so the finally block can close it on every exit path.
          let streamController: AbortController | null = null;
          try {
              setIsExecuting(true);
              posthog.capture("Cotizar lista");
              // Pass excluded item IDs (checked items) to the backend
              const excludedItemIds = Array.from(checkedItemIds);
              // Pass local quantity overrides for non-excluded items
              const itemQuantities: Record<string, number> = {};
              effectiveItems
                  .filter((item) => !checkedItemIds.has(item.id))
                  .forEach((item) => {
                      if (localQuantities[item.id] !== undefined) {
                          itemQuantities[item.id] = localQuantities[item.id];
                      }
                  });

              // Build temporary_items payload from free-text temporary items (catalogProductId === null)
              const temporaryItemsPayload = temporaryItems
                  .filter((item) => item.catalogProductId === null)
                  .map((item) => ({
                      tempKey: item.key,
                      rawDescription: item.rawDescription ?? item.name,
                      quantity: item.quantity,
                  }));

              // The SSE stream endpoint is the ONLY execution request in this
              // flow: it runs the resolution pipeline, streams progress, and
              // delivers the authoritative ListExecution inside its terminal
              // completed event. No parallel JSON execute request is fired.
              const { controller, result } = startProgress(listUuid ?? "", {
                  excludedItemIds,
                  itemQuantities,
                  temporaryItems: temporaryItemsPayload,
              });
              streamController = controller;

              const execution = await result;
              if (!execution) {
                  // Stream ended without a terminal result (failed/early close).
                  toast.error("No se pudo cotizar la lista. Intenta de nuevo.");
                  return;
              }

              setOpenCartSheet(true);

              await applyListExecution(execution);
              setResolutionMode(execution);

              // Linked temporary items (user manually picked a product) —
              // add directly to cart since they already have a catalogProductId.
              await Promise.all(temporaryItems
                  .filter((item) => item.catalogProductId !== null)
                  .map(async (item) => {
                      await addDeletefromCartWithTracking({
                          id: item.catalogProductId!,
                          sku: item.sku ?? String(item.catalogProductId),
                          name: item.name,
                          category_id: item.categoryId,
                          quantity: item.quantity,
                          productDecision: {
                              productId: item.catalogProductId!,
                              action: "no-replacement",
                              replacementIds: [],
                          },
                      });
                  }));
              if (execution.id) {
                  void (async () => {
                      try {
                          await triggerBatchProductUpdates(execution.id);
                      } catch {
                          // Fire-and-forget: list execution and cart opening must not depend on reception acknowledgement.
                      }
                  })();
              }
              navigate("/store");
          } catch (error) {
              // Cut 3: the backend quota gate blocks the execution with a 403
              // plan_required body BEFORE the stream opens. Surface the upsell
              // dialog (with quota-specific copy) instead of the generic error
              // toast.
              if (error instanceof PlanRequiredError) {
                  setQuotaError(error.body);
                  return;
              }
              toast.error("No se pudo cotizar la lista. Intenta de nuevo.");
          } finally {
              // Close the SSE channel on every exit path (success, failure, abort).
              streamController?.abort();
              setIsExecuting(false);
          }
      };

      /**
       * Executes the list: resolves free-text items via LLM in the background and
       * adds linked items to the cart immediately. Opens the CartSheet when done.
       * Excludes items that the user has marked via checkboxes.
       * Guards against cart duplication: shows a confirmation dialog when the cart
       * already has items.
       */
      const handleExecuteList = async () => {
          if (!listUuid || isExecuting) return;
          // Persist any in-progress inline edit before executing
          if (isEditMode) await handleSaveInlineEdit();
          // Guard: if cart has items, ask user to confirm replacement
          if (cartRef.current.length > 0) {
              setShowReplaceDialog(true);
              return;
          }
          await performExecution();
      };

      // ── Item handlers ──────────────────────────────────────────────

      const handleAddItem = async (data: CreateListItemPayload) => {
          if (!listUuid) return;

          // Optimistic UI: show the item immediately so the user knows it was accepted,
          // even though the backend save runs in the background.
          const key = `optimistic-${crypto.randomUUID()}`;
          const optimisticItem: OptimisticListItem = {
              key,
              rawDescription: data.rawDescription,
              catalogProductId: data.catalogProductId ?? null,
              catalogProductSku: data.catalogProductSku ?? null,
              categoryId: data.categoryId ?? null,
              quantity: data.quantity,
              unit: data.unit ?? null,
              name: data.name ?? data.rawDescription,
              type: (data.catalogProductId || data.catalogProductSku)
                  ? ListItemType.LINKED
                  : ListItemType.FREE,
              status: "pending",
          };
          setOptimisticItems((prev) => [...prev, optimisticItem]);

          const savePromise = addItem(listUuid, data);
          pendingInlineAddPromisesRef.current.add(savePromise);
          try {
              await savePromise;
              // The real item is appended to selectedList.items by the lists context —
              // drop the placeholder so it isn't shown twice.
              setOptimisticItems((prev) => prev.filter((i) => i.key !== key));
          } catch {
              setOptimisticItems((prev) =>
                  prev.map((i) => (i.key === key ? { ...i, status: "error" } : i))
              );
          } finally {
              pendingInlineAddPromisesRef.current.delete(savePromise);
          }
      };

      /**
       * Dismisses an optimistic item row (only offered when its save failed).
       * @param key - The temporary key of the optimistic item to remove
       */
      const handleRemoveOptimisticItem = (key: string) => {
          setOptimisticItems((prev) => prev.filter((i) => i.key !== key));
      };

      /**
       * Adds a product as a temporary quote item (view mode only).
       * - When catalogProductId is set, creates a linked temporary item (existing behavior).
       * - When catalogProductId is null, creates a free-text temporary item with rawDescription.
       * Only skips when catalogProductId is undefined (not provided at all).
       * @param data - Payload from InlineProductSearch (catalogProductId may be null for free-text)
       */
      const handleAddTemporaryItem = async (data: CreateListItemPayload): Promise<void> => {
          if (data.catalogProductId === undefined) return;
          const newItem: TemporaryQuoteItem = {
              key: crypto.randomUUID(),
              catalogProductId: data.catalogProductId,
              sku: data.catalogProductSku ?? null,
              name: data.name ?? data.rawDescription,
              categoryId: data.categoryId ?? 0,
              imageUrl: null,
              quantity: data.quantity,
              rawDescription: data.catalogProductId === null ? data.rawDescription : undefined,
          };
          setTemporaryItems((prev) => [...prev, newItem]);
      };


      /**
       * Updates the quantity of a temporary quote item.
       * Removing at qty 1 deletes the item.
       * @param key - The key of the item to update
       * @param newQty - New quantity value
       */
      const handleUpdateTemporaryQuantity = (key: string, newQty: number) => {
          if (newQty <= 0) {
              setTemporaryItems((prev) => prev.filter((item) => item.key !== key));
          } else {
              setTemporaryItems((prev) =>
                  prev.map((item) => item.key === key ? { ...item, quantity: newQty } : item)
              );
          }
      };

      const handleDeleteItem = async (itemId: string) => {
          if (!listUuid) return;
          try {
              await deleteItem(listUuid, itemId);
          } catch {
              toast.error("No se pudo eliminar el producto. Intenta de nuevo.");
          }
      };

      const handleDeleteList = async () => {
          if (!listUuid || isDeletingList) return;
          try {
              setIsDeletingList(true);
              await deleteList(listUuid);
              toast.success("Lista eliminada.");
              navigate(-1);
          } catch {
              toast.error("No se pudo eliminar la lista. Intenta de nuevo.");
          } finally {
              setIsDeletingList(false);
              setShowDeleteListDialog(false);
          }
      };

      // ── Quick-entry: seed example list flow ────────────────────────────────

      /**
       * Seed failures arrive as message-only Errors: the ApiClient throws
       * `Error(detail)` when the backend sends a string detail, otherwise
       * `Error("HTTP <status>: <statusText>")`. Classify by matching the
       * backend's actual details OR the numeric status when present.
       */
      type SeedErrorKind = "forbidden" | "conflict" | "unprocessable" | "unknown";

      const classifySeedError = (error: unknown): SeedErrorKind => {
          const message = error instanceof Error ? error.message : String(error);
          if (/\b403\b/.test(message) || /forbidden/i.test(message)) return "forbidden";
          if (/\b409\b/.test(message) || /default template/i.test(message)) return "conflict";
          if (/\b422\b/.test(message) || /unknown list template/i.test(message)) return "unprocessable";
          return "unknown";
      };

      /** Toast copy per seed failure kind (403 navigates instead of toasting). */
      const SEED_ERROR_COPY: Record<Exclude<SeedErrorKind, "forbidden">, string> = {
          conflict: "No se pudo agregar la lista de ejemplo. Intenta de nuevo.",
          unprocessable: "La lista de ejemplo no está disponible por ahora. Intenta de nuevo.",
          unknown: "No se pudo cargar la lista de ejemplo. Intenta de nuevo.",
      };

      /**
       * Runs the seed request exactly once per user action (ref-guarded) and
       * merges the returned detail via the context so the list refreshes.
       */
      const performSeed = useCallback(async (): Promise<void> => {
          if (!listUuid || isSeedingRef.current) return;
          isSeedingRef.current = true;
          setIsSeeding(true);
          try {
              await seedListTemplate(listUuid, "onboarding-default");
              toast.success("Agregamos productos de ejemplo a tu lista.");
          } catch (error) {
              const kind = classifySeedError(error);
              if (kind === "forbidden") {
                  toast.error("Esta lista ya no está disponible.");
                  navigate("/lists");
                  return;
              }
              toast.error(SEED_ERROR_COPY[kind]);
          } finally {
              isSeedingRef.current = false;
              setIsSeeding(false);
          }
      }, [listUuid, seedListTemplate, navigate]);

      /**
       * Quick-entry seed action: seeds immediately on an empty list, otherwise
       * asks for confirmation first (seeding appends; items may duplicate).
       */
      const handleSeedExampleList = useCallback((): void => {
          if (isSeedingRef.current || !listUuid) return;
          if (hasListItems) {
              setShowSeedConfirm(true);
              return;
          }
          void performSeed();
      }, [hasListItems, performSeed, listUuid]);

      // ── View-mode checklist handlers (≠ edit mode, never persist to DB) ─────

      /**
       * Updates item quantity locally. Reaching 0 moves the item to the excluded section.
       */
      const handleViewModeQuantityChange = (item: ListItem, newQty: number) => {
          if (newQty <= 0) {
              setLocalQuantities((prev) => ({ ...prev, [item.id]: 0 }));
              setCheckedItemIds((prev) => new Set(prev).add(item.id));
          } else {
              setLocalQuantities((prev) => ({ ...prev, [item.id]: newQty }));
          }
      };

      /**
       * Toggles an item’s excluded status. Unchecking a qty-0 item restores its quantity to 1.
       */
      const handleToggleCheck = (itemId: string) => {
          setCheckedItemIds((prev) => {
              const next = new Set(prev);
              if (next.has(itemId)) {
                  next.delete(itemId);
                  // If the item reached the excluded section by qty=0, restore to 1
                  setLocalQuantities((q) => {
                      if ((q[itemId] ?? 1) === 0) return { ...q, [itemId]: 1 };
                      return q;
                  });
              } else {
                  next.add(itemId);
              }
              return next;
          });
      };

      // ── Not found state ────────────────────────────────────────────

      if (!selectedList) {
          return (
              <>
                  <div className="flex flex-col items-center justify-center py-20 gap-4">
                      <ListTodo className="h-12 w-12 opacity-30" />
                      <p className="text-sm text-muted-foreground">Lista no encontrada.</p>
                      <Button variant="ghost" onClick={() => navigate("/lists")}>
                          <ArrowLeft className="h-4 w-4 mr-1" /> Volver
                      </Button>
                  </div>
              </>
          );
      }

      // ── Render ─────────────────────────────────────────────────────

      return (
          <>
              <div className="relative flex flex-col w-full h-full gap-4 pb-28 pt-0 lg:px-8 lg:py-4 overflow-x-hidden lg:pb-4">
                  {/* Main-flow orientation stepper — first child, above all list content (desktop + mobile).
                      FIXED step 1: this screen (create/confirm list) IS the "Crea tu lista" stage; the
                      step is never derived from memory state. The quote in-flight is covered by the
                      ListExecuteProgressOverlay, so isExecuting is intentionally not passed here. */}
                  <MainFlowSteps currentStep={1} />
                  <div className="flex flex-col lg:flex-row lg:mx-auto lg:gap-16 w-full">
                    <div className="flex flex-col w-full lg:w-2/5 gap-2 px-6 lg:ml-24">
                      {/* Name + description + edit controls */}
                      <div className={cn("flex flex-col mb-5",{"mb-0":checkedItems.length > 0} )}>
                          <div className="flex items-center justify-between gap-2">
                              {isEditMode ? (
                                  <input
                                      className="border-input bg-transparent text-2xl font-semibold outline-none focus:border-primary flex-1"
                                      value={editName}
                                      onChange={(e) => setEditName(e.target.value)}
                                      placeholder="Nombre de la lista"
                                  />
                              ) : (
                                  <>
                                      <h1 className="text-2xl font-semibold">{selectedList?.name ?? ""}</h1>
                                      <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Abrir opciones de lista">
                                                  <Ellipsis className="h-4 w-4" />
                                              </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                              <DropdownMenuItem onClick={handleEnterEditMode}>
                                                  <Pencil className="h-4 w-4 mr-2" />
                                                  Editar
                                              </DropdownMenuItem>
                                              <DropdownMenuItem variant="destructive" onClick={() => setShowDeleteListDialog(true)}>
                                                  <Trash className="h-4 w-4 mr-2" />
                                                  Eliminar
                                              </DropdownMenuItem>
                                          </DropdownMenuContent>
                                      </DropdownMenu>
                                  </>
                              )}
                          </div>
                          <div className="flex flex-col gap-1 mt-1">
                              {
                                  (!isEditMode)&&(
                                  <div className="flex gap-2 text-muted-foreground">
                                      

                                      {checkedItems.length > 0 && (
                                          <>
                                          
                                          <Badge variant={"outline"}>{checkedItems.length} excluido{checkedItems.length !== 1 ? "s" : ""}</Badge>
                                          </>
                                      )}
                                  </div>
                                  )
                              }
                          </div>                        
                      </div>

                      {/* Inline product search — edit mode adds to the list, view mode adds temporary quote items */}
                      {isEditMode ? (
                        <div className=" flex flex-col gap-2 mb-2">
                          <InlineProductSearch
                            label="Escribe lo que necesitas. Nosotros buscamos los productos"
                            onAdd={handleAddItem}
                            onSeedExampleList={handleSeedExampleList}
                            onOpenEmailImport={() => setEmailImportOpen(true)}
                            enableSuggestionBadges
                          />
                          {showEmptyListState && listUuid ? <EmailImportCard listUuid={listUuid} /> : null}
                          {onboardingTargets ? <TargetListProducts /> : null}
                        </div>
                    ) : (
                        <div className="mb-2">
                            {/* <div className="flex items-center gap-2 mb-3">
                                <div className="flex-1 h-px bg-border" />
                                <span className="text-xs font-medium text-muted-foreground shrink-0">
                                    Agregar solo para cotizar
                                </span>
                                <div className="flex-1 h-px bg-border" />
                            </div> */}
                            <InlineProductSearch
                                label="¿Quieres agregar algo más?"
                                onAdd={handleAddTemporaryItem}
                                onOpenEmailImport={() => setEmailImportOpen(true)}
                            />
                            {temporaryItems.length > 0 && (
                                <div className="flex flex-col gap-1.5 mt-3">
                                    {temporaryItems.map((item) => (
                                        <div
                                            key={item.key}
                                            className="flex items-center justify-between gap-3 rounded-lg border border-dashed bg-muted/30 px-3 py-2"
                                        >
                                            <div className="flex flex-col min-w-0 flex-1">
                                                <span className="text-sm font-medium truncate">{item.name}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    {item.catalogProductId === null
                                                        ? "Sin preferencia — el optimizador elegirá"
                                                        : "Solo para esta compra · no se guarda en tu lista"}
                                                </span>
                                            </div>
                                            <AddDeleteList
                                                currQuantity={item.quantity}
                                                onIncrement={() => handleUpdateTemporaryQuantity(item.key, item.quantity + 1)}
                                                onDecrement={() => handleUpdateTemporaryQuantity(item.key, item.quantity - 1)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {!isEditMode && (temporaryItems.length > 0 || Object.keys(localQuantities).length > 0 || checkedItemIds.size > 0) && (
                        <div className="flex items-center justify-between rounded-lg border border-dashed border-orange-300/10 bg-orange-50/20 px-4 py-2">
                            <span className="text-sm text-muted-foreground">
                                Los cambios son temporales · no se guardan en tu lista
                            </span>
                            <Button variant="outline" size="sm" onClick={handleSaveTemporary}>
                                Guardar
                            </Button>
                        </div>
                    )}

                    {/* Items list */}
                    <div className="flex flex-col gap-2 w-full min-w-0 max-w-full overflow-hidden">
                        {showEmptyListState ? (
                            <Empty>
                            <EmptyHeader>
                                <EmptyMedia variant="icon">
                                <Milk />
                                </EmptyMedia>
                                <EmptyTitle>No hay productos en la lista</EmptyTitle>
                                <EmptyDescription>
                                Escribe lo que necesitas, <span className="font-semibold">nosotros encontramos el mejor precio.</span>
                                </EmptyDescription>
                            </EmptyHeader>
                            </Empty>
                        ) : (
                            <>
                                {activeItems.length > 0 ? (
                                    // Both view and edit mode: grouped by category. Optimistic
                                    // in-flight adds are rendered first, then confirmed items.
                                    <>
                                        {optimisticItems.map((item) => (
                                            <OptimisticItemRow
                                                key={item.key}
                                                item={item}
                                                onRemove={() => handleRemoveOptimisticItem(item.key)}
                                            />
                                        ))}
                                        {groupListItemsByCategory(activeItems).map((group) => (
                                            <div key={group.category.id} className="w-full mt-0 first:mt-0">
                                                <div className="mb-1 flex items-center gap-2 rounded-md  px-2 pt-1 pb-0 text-sm font-semibold text-muted-foreground">
                                                    <MenuIcon iconName={group.category.icon} size={17} className="text-primary" />
                                                    <span>{group.category.name}</span>
                                                </div>
                                                <div className={cn("flex flex-col gap-0 w-full pl-8",{"pl-2":!isDesktop})}>
                                                    {group.items.map((item) => (
                                                        <ListItemRow
                                                            key={item.id}
                                                            item={item}
                                                            isEditMode={isEditMode}
                                                            isChecked={false}
                                                            saveError={saveErrors[item.id]}
                                                            onEdit={(it) => {
                                                                setEditingItem(it);
                                                                setAddItemOpen(true);
                                                            }}
                                                            onDelete={handleDeleteItem}
                                                            onQuantityChange={(it, newQty) =>
                                                                isEditMode
                                                                    ? autosaveEditItem(it.id, {
                                                                          rawDescription: it.rawDescription,
                                                                          catalogProductId: it.catalogProductId,
                                                                          quantity: newQty,
                                                                          unit: it.unit,
                                                                          notes: it.notes,
                                                                      })
                                                                    : handleViewModeQuantityChange(it, newQty)
                                                            }
                                                            onToggleCheck={() => handleToggleCheck(item.id)}
                                                            onRawDescriptionChange={
                                                                isEditMode
                                                                    ? (itemId, newRawDescription) =>
                                                                          autosaveEditItem(itemId, {
                                                                              rawDescription: newRawDescription,
                                                                              catalogProductId: item.catalogProductId,
                                                                              quantity: item.quantity,
                                                                              unit: item.unit,
                                                                              notes: item.notes,
                                                                          })
                                                                    : undefined
                                                            }
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    // No confirmed items — render in-flight optimistic adds only
                                    <>
                                        {optimisticItems.map((item) => (
                                            <OptimisticItemRow
                                                key={item.key}
                                                item={item}
                                                onRemove={() => handleRemoveOptimisticItem(item.key)}
                                            />
                                        ))}
                                    </>
                                )}
                                {checkedItems.length > 0 && (
                                    <>
                                        <div className="flex items-center gap-2 mt-3 mb-1">
                                            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                                                No incluir en la compra
                                            </span>
                                            <div className="flex-1 h-px bg-border" />
                                        </div>
                                        {checkedItems.map((item) => (
                                            <ListItemRow
                                                key={item.id}
                                                item={item}
                                                isEditMode={isEditMode}
                                                isChecked={true}
                                                saveError={saveErrors[item.id]}
                                                onEdit={(it) => {
                                                    setEditingItem(it);
                                                    setAddItemOpen(true);
                                                }}
                                                onDelete={handleDeleteItem}
                                                onToggleCheck={() => handleToggleCheck(item.id)}
                                                onRawDescriptionChange={(itemId, newRawDescription) =>
                                                    autosaveEditItem(itemId, {
                                                        rawDescription: newRawDescription,
                                                        catalogProductId: item.catalogProductId,
                                                        quantity: item.quantity,
                                                        unit: item.unit,
                                                        notes: item.notes,
                                                    })
                                                }
                                            />
                                        ))}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {isDesktop && (displayItems.length > 0 || isEditMode) && (
                    <div className="lg:w-80 shrink-0 px-6 flex flex-col text-sm gap-1 hidden lg:flex" data-testid="desktop-execute-cta-container">
                        <h2 className="font-semibold text-lg mb-2">Resumen de tu lista</h2>
                        <span className="text-muted-foreground font-semibold inline-flex"><ShoppingBasket className="text-primary w-4 h-4 mr-2"/> Productos incluidos: <span className="text-foreground ml-2">{includedCount}</span></span>
                        <span className="text-muted-foreground font-semibold inline-flex"><PackageMinus className="text-destructive w-4 h-4 mr-2"/> Omitidos: <span className="text-foreground ml-2">{excludedCount}</span></span>
                        <span className="text-muted-foreground font-semibold inline-flex"><Sparkles className="best-price w-4 h-4 mr-2"/> Sin marca de preferencia: <span className="text-foreground ml-2">{noPreferenceCount}</span></span>
                        <span className="text-muted-foreground font-semibold inline-flex"><LinkIcon className="text-foreground w-4 h-4 mr-2"/> Marca fija: <span className="text-foreground ml-2">{fixedBrandCount}</span></span>
                        <div className="mt-4">
                            <Button
                                variant="default"
                                size="lg"
                                className="w-full"
                                disabled={(isEditMode && isSavingList) || (!isEditMode && isExecuting)}
                                onClick={isEditMode ? handleSaveInlineEdit : handleExecuteList}
                                data-testid="desktop-execute-cta"
                            >
                                {isEditMode ? <Check className="h-4 w-4 mr-1" /> : <PackageSearch className="h-4 w-4 mr-1" />}
                                <SlotText text={isEditMode ? (isSavingList ? "Guardando..." : "Guardar lista") : executeCtaLabel} />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

                <div className="w-full fixed bottom-0 left-0 p-4 bg-background border-t lg:hidden" data-testid="mobile-execute-cta-container">
                {displayItems.length > 0 || isEditMode ? (
                    <Button
                        variant="default"
                        size="lg"
                        className="w-full my-2 text-base"
                        disabled={(isEditMode && isSavingList) || (!isEditMode && isExecuting)}
                        onClick={isEditMode ? handleSaveInlineEdit : handleExecuteList}
                    >
                        {isEditMode ? <Check className="h-4 w-4 mr-1" /> : <PackageSearch className="h-4 w-4 mr-1" />}
                        <SlotText text={isEditMode ? (isSavingList ? "Guardando..." : "Guardar lista") : executeCtaLabel} />
                    </Button>
                ) : null}
                </div>
            </div>

            {/* <AddItemDialog
                open={addItemOpen}
                onOpenChange={setAddItemOpen}
                initialData={
                    editingItem
                        ? {
                            id: editingItem.id,
                            rawDescription: editingItem.rawDescription,
                            catalogProductId: editingItem.catalogProductId,
                            quantity: editingItem.quantity,
                            unit: editingItem.unit,
                            notes: editingItem.notes,
                        }
                        : undefined
                }
                onAdd={editingItem ? handleEditItem : handleAddItem}
            /> */}

            <AlertDialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Reemplazar carrito actual?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Tu carrito tiene {cart.length} producto{cart.length !== 1 ? "s" : ""}.
                            Al cotizar esta lista se eliminarán los productos actuales.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { clearCart(); setShowReplaceDialog(false); setTimeout(() => handleExecuteList(), 0); }}>
                            Reemplazar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showDeleteListDialog} onOpenChange={setShowDeleteListDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar lista?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta acción eliminará "{selectedList.name}" y no se puede deshacer.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeletingList}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={isDeletingList}
                            onClick={handleDeleteList}
                        >
                            {isDeletingList ? "Eliminando..." : "Eliminar"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/*
              * Seed confirmation gate for non-empty lists. Seeding APPENDS to the
              * list (the backend skips already-seeded lists, but there is no
              * content-level dedup) — the copy must not promise deduplication.
              */}
            <AlertDialog open={showSeedConfirm} onOpenChange={setShowSeedConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Agregar lista de ejemplo?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Se agregarán productos de ejemplo a tu lista actual. Tus productos no se reemplazan ni eliminan; algunos pueden quedar duplicados.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={isSeeding}
                            onClick={() => {
                                setShowSeedConfirm(false);
                                void performSeed();
                            }}
                        >
                            Agregar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Single controlled email-import popup — opened from the quick-entry menu. */}
            {listUuid ? (
                <EmailImportDialog
                    open={emailImportOpen}
                    onOpenChange={setEmailImportOpen}
                    listUuid={listUuid}
                />
            ) : null}

            {isExecuting && (
                <ListExecuteProgressOverlay isExecuting={isExecuting} progress={progress} />
            )}

            {showMigrationOverlay && (
                <ListMigrateProgressOverlay
                    status={migrationStream.status === "error" ? "error" : "streaming"}
                    percentage={migrationStream.state?.percentage ?? 0}
                    message={migrationStream.state?.message}
                    failedCount={migrationStream.state?.failedCount ?? 0}
                    onRetry={migrationStream.retry}
                />
            )}

            {/* Cut 3: quota block — 403 plan_required surfaced as the upsell dialog. */}
            <AcquirePlusDialog
                open={quotaError !== null}
                onOpenChange={(open) => {
                    if (!open) setQuotaError(null);
                }}
                reason={quotaError?.reason}
                quota={quotaError?.quota}
            />
        </>
    );
};

const TargetListProducts = () => {
  return(
    <div className="flex items-start gap-2 rounded-lg border border-dashed border-orange-300 bg-orange-50/50 px-4 py-3 text-sm text-muted-foreground">
      <Info className="h-4 w-4 shrink-0 text-orange-400" />
      <span>Esta es tu lista inicial. Agrega tus productos y te mostramos cuánto ahorras.</span>
    </div>
  );
}
export { ListDetailPage };
